import * as yup from 'yup';
import { useState } from 'react';
import { Formik } from 'formik';
import { DropDown, ChipInput, ChipInputTagErrors } from '@qrios/frontend-library';

import { FILTERS, OPERATORS, PAGE_SIZES, FilterFormProps, WithFilterFormLayout } from '../../../../../common/filters';
import { FORM_INPUT_ERROR_MSGS } from '../../../../../../constants/errors';
import DateRangePicker from '../../../../../common/fields/date-range-picker';
import { billingEndDateValidation, msisdnValidationSimple } from '../../../../../../constants/validation';

import locales from '../reports/locales';
import { VoiceBillingFilters } from './interfaces';
import { activeFiltersCountMapper, initFilters } from './filters';
import {
  convertReadableToVoiceSessionCloseReasonQuery,
  convertVoiceSessionCloseReasonQueryToReadableFormat
} from '../../../../../common/table/utils/operationTypeFormatters';
import { VOICE_SESSION_CLOSE_REASONS_QUERY } from '../../../../../common/filters/constants';

const validationSchema = yup.object().shape({
  from: yup.date().required().typeError(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('from date')),
  to: billingEndDateValidation,
  msisdn: yup.array(msisdnValidationSimple)
});

const FilterForm = ({ onSubmit, onClose, activeFilters }: FilterFormProps<VoiceBillingFilters>) => {
  const initialFiltersDifferentThanActiveFilters = activeFiltersCountMapper(activeFilters) !== 0;
  const [filtersChanged, setFilterChanged] = useState<boolean>(initialFiltersDifferentThanActiveFilters);
  const [initialValues, setInitialValues] = useState<VoiceBillingFilters>({
    from: activeFilters.from,
    to: activeFilters.to,
    app_id: activeFilters.app_id,
    operator: activeFilters.operator,
    operation_id: activeFilters.operation_id,
    msisdn: activeFilters.msisdn,
    session_tag: activeFilters.session_tag,
    session_close_reason: activeFilters.session_close_reason,
    page_size: activeFilters.page_size
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(filters: VoiceBillingFilters) => {
        onSubmit({
          ...filters,
          msisdn: filters.msisdn
        });
      }}
    >
      {({ values, handleSubmit, setFieldValue, errors, touched, handleBlur }) => {
        const handleChange = (field: string, value: unknown) => {
          setFieldValue(field, value);
          setFilterChanged(true);
        };

        const handleQueryTypesChange = (field: string, value: string[]) => {
          const convertedValue = value.map(reason => convertReadableToVoiceSessionCloseReasonQuery(reason));
          setFieldValue(field, convertedValue);
        };

        const closeOrClearFilters = () => {
          if (!filtersChanged) {
            onClose();
          }
          setInitialValues(initFilters);
          setFilterChanged(false);
        };

        return (
          <WithFilterFormLayout
            onSecondary={closeOrClearFilters}
            onSubmit={handleSubmit}
            filtersChanged={filtersChanged}
          >
            <DateRangePicker
              from={{ name: FILTERS.FROM, value: values.from, error: errors.from, touched: touched.from }}
              to={{ name: FILTERS.TO, value: values.to, error: errors.to, touched: touched.to }}
              handleFromChange={value => handleChange(FILTERS.FROM, value)}
              handleToChange={value => handleChange(FILTERS.TO, value)}
              handleBlur={handleBlur}
              variant="nowEnabled"
            />
            <DropDown
              label={locales.filtersForm.operators.label}
              value={values.operator}
              options={OPERATORS}
              name={FILTERS.OPERATOR}
              handleBlur={handleBlur}
              handleChange={handleChange}
              multiple={true}
              placeholder={locales.filtersForm.operators.placeholder}
            />
            <ChipInput
              error={errors.app_id?.[(errors.app_id ?? []).length - 1]}
              label={locales.filtersForm.appIds.label}
              value={values.app_id}
              name={FILTERS.APP_ID}
              handleBlur={handleBlur}
              handleChange={handleChange}
              placeholder={locales.filtersForm.appIds.placeholder}
              tagErrors={errors.app_id as ChipInputTagErrors}
            />
            <ChipInput
              error={errors.operation_id?.[(errors.operation_id ?? []).length - 1]}
              label={locales.filtersForm.operationIds.label}
              value={values.operation_id}
              name={FILTERS.OPERATION_ID}
              handleBlur={handleBlur}
              handleChange={handleChange}
              placeholder={locales.filtersForm.operationIds.placeholder}
              tagErrors={errors.operation_id as ChipInputTagErrors}
            />
            <ChipInput
              handleChange={handleChange}
              name={FILTERS.MSISDN}
              label={locales.filtersForm.phoneNumbers.label}
              placeholder={locales.filtersForm.phoneNumbers.placeholder}
              handleBlur={handleBlur}
              value={values.msisdn}
              error={errors.msisdn?.[(errors.msisdn ?? []).length - 1]}
              tagErrors={errors.msisdn as ChipInputTagErrors}
            />
            <ChipInput
              handleChange={handleChange}
              name={FILTERS.SESSION_TAG}
              label={locales.filtersForm.sessionTags.label}
              placeholder={locales.filtersForm.sessionTags.placeholder}
              handleBlur={handleBlur}
              value={values.session_tag}
              error={errors.session_tag?.[(errors.session_tag ?? []).length - 1]}
              tagErrors={errors.session_tag as ChipInputTagErrors}
              helperText={locales.filtersForm.sessionTags.helperText}
            />
            <DropDown
              label={locales.filtersForm.sessionCloseReasons.label}
              value={values.session_close_reason.map(reason =>
                convertVoiceSessionCloseReasonQueryToReadableFormat(reason)
              )}
              options={VOICE_SESSION_CLOSE_REASONS_QUERY.map(reason =>
                convertVoiceSessionCloseReasonQueryToReadableFormat(reason)
              )}
              name={FILTERS.SESSION_CLOSE_REASON}
              handleBlur={handleBlur}
              handleChange={handleQueryTypesChange}
              multiple={true}
              placeholder={locales.filtersForm.sessionCloseReasons.placeholder}
            />
            <DropDown
              label="Number of results to display at page"
              value={values.page_size}
              options={PAGE_SIZES}
              name={FILTERS.PAGE_SIZE}
              handleBlur={handleBlur}
              handleChange={handleChange}
              disableClearable={true}
            />
          </WithFilterFormLayout>
        );
      }}
    </Formik>
  );
};

export default FilterForm;
